import { useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X, SlidersHorizontal, ArrowUp, ArrowDown, Search, Layers } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth";
import { costMatrixApi } from "@/features/finances/api/finances";
import { GroupedCostMatrix } from "@/features/finances/components/GroupedCostMatrix";
import { formatAmount } from "@/utils/formatAmount";
import { PageLoader } from "@/components/ui/Spinner";
import type { ActivitySegment, ProjectStatus } from "@/features/finances/types";
import { useClickOutside } from "@/hooks/useClickOutside";

type SortField = "project" | "status" | "activity" | "salary" | "other" | "total";
type SortDir = "asc" | "desc";

const TODAY = new Date();
const CURRENT_YEAR = TODAY.getFullYear();
const CURRENT_MONTH = TODAY.getMonth() + 1;

const MONTHS_LONG = [
  "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
  "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień",
];
const MONTHS_SHORT = ["Sty","Lut","Mar","Kwi","Maj","Cze","Lip","Sie","Wrz","Paź","Lis","Gru"];

const STATUS_CFG: Record<ProjectStatus, { label: string; cls: string; order: number }> = {
  active:    { label: "Aktywne",    cls: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",    order: 0 },
  suspended: { label: "Wstrzymane", cls: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300", order: 1 },
  inactive:  { label: "Nieaktywne", cls: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",           order: 2 },
};

function StatusBadge({ status }: { status: ProjectStatus }) {
  const c = STATUS_CFG[status] ?? STATUS_CFG.inactive;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${c.cls}`}>{c.label}</span>;
}

const SEG_LABELS: Record<string, string> = { active: "akt", suspended: "zaw", inactive: "brak" };
const SEG_COLORS: Record<string, string> = {
  active: "text-green-600 dark:text-green-400",
  suspended: "text-yellow-600 dark:text-yellow-400",
  inactive: "text-gray-400 dark:text-gray-500",
};

function fmtDay(day: number, month: number) {
  return `${String(day).padStart(2, "0")}.${String(month).padStart(2, "0")}`;
}

function ActivityBar({ pct, segments, month }: { pct: number | null; segments?: ActivitySegment[] | null; month?: number }) {
  if (pct === null) return <span className="text-xs text-gray-300 dark:text-gray-600 italic">—</span>;
  const color = pct >= 90 ? "bg-green-500" : pct >= 50 ? "bg-yellow-400" : "bg-red-400";
  const showSegs = segments && segments.length >= 1 && pct > 0 && pct < 100 && month;
  return (
    <div className="flex flex-col gap-0.5 min-w-[140px]">
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
          <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
        </div>
        <span className="text-xs tabular-nums text-gray-600 dark:text-gray-300 w-9 text-right">{pct.toFixed(0)}%</span>
      </div>
      {showSegs && (
        <div className="text-[10px] leading-snug space-y-px">
          {segments!.map((s, i) => (
            <span key={i} className={`block ${SEG_COLORS[s.status] ?? "text-gray-400"}`}>
              {fmtDay(s.from_day, month!)}–{fmtDay(s.to_day, month!)} {SEG_LABELS[s.status] ?? s.status}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) return <span className="ml-1 opacity-20">↕</span>;
  return <span className="ml-1 text-[var(--color-primary)]">{dir === "asc" ? "↑" : "↓"}</span>;
}

function Pln({ value }: { value: string | null | number }) {
  const n = value === null || value === undefined ? 0 : parseFloat(String(value));
  if (!n) return <span className="font-normal text-gray-300 dark:text-gray-600">—</span>;
  return <>{formatAmount(String(n.toFixed(2)))} <span className="text-xs font-normal text-gray-400">PLN</span></>;
}

/* ── MonthPicker ── */

function MonthPicker({ year, month, onChange }: {
  year: number;
  month: number | undefined;
  onChange: (year: number, month: number | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const [gridYear, setGridYear] = useState(year);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));

  function openPicker() { setGridYear(year); setOpen(true); }
  function pick(m: number) { onChange(gridYear, m); setOpen(false); }
  function pickYear() { onChange(gridYear, undefined); setOpen(false); }

  function shift(delta: number) {
    if (open) { setGridYear(y => y + delta); return; }
    if (month === undefined) { onChange(year + delta, undefined); return; }
    let ny = year, nm = month + delta;
    if (nm < 1) { ny--; nm = 12; }
    if (nm > 12) { ny++; nm = 1; }
    onChange(ny, nm);
  }

  const label = month ? `${MONTHS_LONG[month - 1]} ${year}` : `Cały rok ${year}`;

  return (
    <div ref={ref} className="relative select-none">
      <div className="flex items-center gap-1">
        <button onClick={() => shift(-1)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition-colors">
          <ChevronLeft size={18} />
        </button>
        <button
          onClick={openPicker}
          className="flex items-center gap-1.5 px-5 py-1.5 rounded-full border border-gray-200 dark:border-gray-600 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 font-semibold text-sm transition-colors min-w-[168px] justify-center"
        >
          {label} <span className="text-[10px] opacity-40">▾</span>
        </button>
        <button onClick={() => shift(+1)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 transition-colors">
          <ChevronRight size={18} />
        </button>
      </div>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-2xl p-4 w-60">
          <button onClick={() => setOpen(false)} className="absolute top-2.5 right-2.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X size={14} />
          </button>
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setGridYear(y => y - 1)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
              <ChevronLeft size={14} />
            </button>
            <span className="font-bold text-sm text-gray-900 dark:text-gray-100">{gridYear}</span>
            <button onClick={() => setGridYear(y => y + 1)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
              <ChevronRight size={14} />
            </button>
          </div>
          <div className="grid grid-cols-4 gap-1 mb-2">
            {MONTHS_SHORT.map((m, i) => {
              const sel = gridYear === year && month === i + 1;
              const cur = gridYear === CURRENT_YEAR && i + 1 === CURRENT_MONTH;
              return (
                <button
                  key={i}
                  onClick={() => pick(i + 1)}
                  className={`py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    sel ? "bg-[var(--color-primary)] text-white" :
                    cur ? "ring-1 ring-[var(--color-primary)] text-[var(--color-primary)] hover:bg-gray-100 dark:hover:bg-gray-700" :
                         "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                >
                  {m}
                </button>
              );
            })}
          </div>
          <button
            onClick={pickYear}
            className={`w-full py-1.5 rounded-lg text-xs font-medium transition-colors border ${
              gridYear === year && month === undefined
                ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-[var(--color-primary)]/5"
                : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            Cały rok {gridYear}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── ProjectMultiSelect ── */

interface ProjectOption {
  id: string;
  name: string;
  short: string;
}

function ProjectMultiSelect({
  options,
  selected,
  onChange,
  disabled,
}: {
  options: ProjectOption[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter(o => o.name.toLowerCase().includes(q) || o.short.toLowerCase().includes(q));
  }, [options, search]);

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  }

  function selectAll() { onChange(new Set(filtered.map(o => o.id))); }
  function clearAll() { onChange(new Set()); }

  const count = selected.size;
  const label = disabled ? "Wczytywanie…" : count === 0 ? "Wszystkie przedsięwzięcia" : `Zaznaczone: ${count}`;

  return (
    <div ref={ref} className="relative flex flex-col gap-1">
      <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Przedsięwzięcia</label>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        className={`flex items-center justify-between gap-2 border rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 min-w-[220px] text-left transition-colors ${
          count > 0
            ? "border-[var(--color-primary)] text-[var(--color-primary)]"
            : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
        } disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        <span className="truncate">{label}</span>
        <span className="text-[10px] opacity-40 shrink-0">▾</span>
      </button>

      {open && !disabled && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-2xl w-72">
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                autoFocus
                type="text"
                placeholder="Szukaj…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-gray-100 placeholder:text-gray-400 outline-none focus:border-[var(--color-primary)]"
              />
            </div>
          </div>

          <div className="flex gap-2 px-2 py-1.5 border-b border-gray-100 dark:border-gray-700">
            <button type="button" onClick={selectAll} className="text-xs text-[var(--color-primary)] hover:underline">
              Zaznacz wszystkie{search ? " pasujące" : ""}
            </button>
            {count > 0 && (
              <>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <button type="button" onClick={clearAll} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:underline">
                  Usuń zaznaczenie
                </button>
              </>
            )}
          </div>

          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-xs text-gray-400 text-center">Brak wyników</div>
            ) : (
              filtered.map(o => (
                <label key={o.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.has(o.id)}
                    onChange={() => toggle(o.id)}
                    className="w-3.5 h-3.5 shrink-0 rounded accent-[var(--color-primary)]"
                  />
                  <span className="font-mono text-[10px] text-gray-400 shrink-0 w-14 truncate">{o.short || "—"}</span>
                  <span className="text-xs text-gray-800 dark:text-gray-200 leading-tight line-clamp-2">{o.name}</span>
                </label>
              ))
            )}
          </div>

          <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center">
            <span className="text-[10px] text-gray-400">{filtered.length} z {options.length}</span>
            <button type="button" onClick={() => setOpen(false)} className="text-xs font-medium text-[var(--color-primary)] hover:underline">
              Gotowe
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── main page ── */

export function OrderCostMatrixPage() {
  const { user } = useAuthStore();
  const isCEO = !!user?.is_ceo;
  const isAccountant = user?.role === "accountant";

  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState<number | undefined>(CURRENT_MONTH);

  const [search, setSearch] = useState("");
  const [hideEmpty, setHideEmpty] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | "">("");
  const [minCost, setMinCost] = useState("");
  const [maxCost, setMaxCost] = useState("");
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
  const [groupBy, setGroupBy] = useState(false);

  const [sortField, setSortField] = useState<SortField>("project");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const showMonthCols = month !== undefined;

  const { data, isLoading } = useQuery({
    queryKey: ["order-cost-matrix", year, month],
    queryFn: () => costMatrixApi.get(year, month),
    enabled: !groupBy, // w trybie grupowym renderuje się osobny komponent z własnym zapytaniem
  });

  // reset project selection when period changes (new project list)
  // done via queryKey dependency in useMemo below

  const availableProjects = useMemo<ProjectOption[]>(() => {
    if (!data?.rows) return [];
    return data.rows.map(r => ({ id: r.project_id, name: r.project_name, short: r.project_short_name }));
  }, [data]);

  const rows = useMemo(() => {
    let r = data?.rows ?? [];
    if (selectedProjects.size > 0) r = r.filter(x => selectedProjects.has(x.project_id));
    if (statusFilter) r = r.filter(x => x.status === statusFilter);
    if (hideEmpty) r = r.filter(x => parseFloat(x.other_cost) > 0 || parseFloat(x.salary_cost ?? "0") > 0);

    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(x => x.project_name.toLowerCase().includes(q) || x.project_short_name.toLowerCase().includes(q));
    }

    const totalCost = (x: typeof r[0]) => parseFloat(x.other_cost) + parseFloat(x.salary_cost ?? "0");
    const min = minCost !== "" ? parseFloat(minCost) : null;
    const max = maxCost !== "" ? parseFloat(maxCost) : null;
    if (min !== null && !isNaN(min)) r = r.filter(x => totalCost(x) >= min);
    if (max !== null && !isNaN(max)) r = r.filter(x => totalCost(x) <= max);

    return [...r].sort((a, b) => {
      let cmp = 0;
      if (sortField === "project")   cmp = (a.project_short_name || a.project_name).localeCompare(b.project_short_name || b.project_name, "pl");
      else if (sortField === "status")   cmp = (STATUS_CFG[a.status]?.order ?? 99) - (STATUS_CFG[b.status]?.order ?? 99);
      else if (sortField === "activity") cmp = (a.days_active_pct ?? -1) - (b.days_active_pct ?? -1);
      else if (sortField === "salary")   cmp = parseFloat(a.salary_cost ?? "0") - parseFloat(b.salary_cost ?? "0");
      else if (sortField === "other")    cmp = parseFloat(a.other_cost) - parseFloat(b.other_cost);
      else if (sortField === "total")    cmp = (parseFloat(a.other_cost) + parseFloat(a.salary_cost ?? "0")) - (parseFloat(b.other_cost) + parseFloat(b.salary_cost ?? "0"));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [data, search, hideEmpty, statusFilter, minCost, maxCost, selectedProjects, sortField, sortDir]);

  const sumSalary = useMemo(() => rows.reduce((s, r) => s + parseFloat(r.salary_cost ?? "0"), 0), [rows]);
  const sumOther  = useMemo(() => rows.reduce((s, r) => s + parseFloat(r.other_cost), 0), [rows]);
  const sumTotal  = sumSalary + sumOther;

  function ts(field: SortField) {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  }

  function clearFilters() {
    setSearch(""); setHideEmpty(false); setStatusFilter(""); setMinCost(""); setMaxCost(""); setSelectedProjects(new Set());
  }

  const hasFilters = search || hideEmpty || statusFilter || minCost || maxCost || selectedProjects.size > 0;
  const activeFilterCount = [statusFilter, minCost, maxCost, hideEmpty ? "1" : "", selectedProjects.size > 0 ? "1" : ""].filter(Boolean).length;

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const sortOptions: { value: SortField; label: string }[] = [
    { value: "project",  label: "Przedsięwzięcie" },
    { value: "status",   label: "Status" },
    ...(showMonthCols ? [{ value: "activity" as SortField, label: "Aktywność" }] : []),
    ...(showMonthCols ? [{ value: "salary"   as SortField, label: "Koszty płac" }] : []),
    { value: "other",    label: "Pozostałe koszty" },
    { value: "total",    label: "Łącznie" },
  ];

  if (isLoading) return <PageLoader />;

  return (
    <div className="flex flex-col gap-4 lg:gap-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">Macierz kosztów</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Koszty płac i pozostałe koszty (koszty wewnętrzne + faktury wykonawców) per przedsięwzięcie.{" "}
            {isCEO || isAccountant ? "Widoczne wszystkie przedsięwzięcia." : "Widoczne przedsięwzięcia z przyznanymi dostępami."}
          </p>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-2">
          <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); setSelectedProjects(new Set()); }} />
          <button
            type="button"
            onClick={() => setGroupBy((g) => !g)}
            className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              groupBy
                ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-[var(--color-primary)]"
            }`}
            title="Zwiń drobne zlecenia do jednego wiersza na grupę"
          >
            <Layers size={14} /> {groupBy ? "Widok: grupy zleceń" : "Zwiń do grup zleceń"}
          </button>
        </div>
      </div>

      {groupBy ? (
        <GroupedCostMatrix year={year} month={month} />
      ) : (
        <>

      {/* Filters — desktop */}
      <div className="hidden lg:flex bg-white dark:bg-gray-800 p-4 flex-wrap gap-3 items-end">
        <ProjectMultiSelect
          options={availableProjects}
          selected={selectedProjects}
          onChange={setSelectedProjects}
          disabled={isLoading}
        />

        <div className="flex flex-col gap-1 flex-1 min-w-[150px]">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Szukaj nazwy / symbolu</label>
          <input
            type="text" placeholder="Nazwa lub symbol..."
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
            value={search} onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</label>
          <select
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
            value={statusFilter} onChange={e => setStatusFilter(e.target.value as ProjectStatus | "")}
          >
            <option value="">Wszystkie</option>
            <option value="active">Aktywne</option>
            <option value="suspended">Wstrzymane</option>
            <option value="inactive">Nieaktywne</option>
          </select>
        </div>

        <div className="flex flex-col gap-1 w-36">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Suma kosztów min</label>
          <input type="number" step="0.01" min="0" placeholder="0,00"
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
            value={minCost} onChange={e => setMinCost(e.target.value)} />
        </div>

        <div className="flex flex-col gap-1 w-36">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Suma kosztów max</label>
          <input type="number" step="0.01" min="0" placeholder="bez limitu"
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
            value={maxCost} onChange={e => setMaxCost(e.target.value)} />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer pb-0.5">
          <input type="checkbox" checked={hideEmpty} onChange={e => setHideEmpty(e.target.checked)} className="w-4 h-4 rounded" />
          Tylko z kosztami
        </label>

        {hasFilters && (
          <button className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline pb-0.5" onClick={clearFilters}>
            Wyczyść
          </button>
        )}
      </div>

      {/* Filters — mobile */}
      <div className="lg:hidden bg-white dark:bg-gray-800 p-3 flex flex-col gap-3">
        <div className="flex gap-2">
          <input
            type="text" placeholder="Szukaj nazwy lub symbolu..."
            className="flex-1 min-w-0 border border-gray-300 dark:border-gray-600 rounded px-2 py-2 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
            value={search} onChange={e => setSearch(e.target.value)}
          />
          <button
            type="button"
            onClick={() => setMobileFiltersOpen(o => !o)}
            className={`shrink-0 px-3 py-2 rounded border text-sm flex items-center gap-1.5 transition-colors ${
              mobileFiltersOpen || activeFilterCount > 0
                ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-[var(--color-primary)]/5"
                : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"
            }`}
            aria-label="Filtry"
          >
            <SlidersHorizontal size={16} />
            {activeFilterCount > 0 && (
              <span className="text-[10px] font-bold bg-[var(--color-primary)] text-white rounded-full w-4 h-4 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {mobileFiltersOpen && (
          <div className="flex flex-col gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <ProjectMultiSelect
              options={availableProjects}
              selected={selectedProjects}
              onChange={setSelectedProjects}
              disabled={isLoading}
            />

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</label>
              <select
                className="border border-gray-300 dark:border-gray-600 rounded px-2 py-2 text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
                value={statusFilter} onChange={e => setStatusFilter(e.target.value as ProjectStatus | "")}
              >
                <option value="">Wszystkie statusy</option>
                <option value="active">Aktywne</option>
                <option value="suspended">Wstrzymane</option>
                <option value="inactive">Nieaktywne</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Koszty min</label>
                <input type="number" step="0.01" min="0" placeholder="0,00"
                  className="border border-gray-300 dark:border-gray-600 rounded px-2 py-2 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
                  value={minCost} onChange={e => setMinCost(e.target.value)} />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Koszty max</label>
                <input type="number" step="0.01" min="0" placeholder="bez limitu"
                  className="border border-gray-300 dark:border-gray-600 rounded px-2 py-2 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
                  value={maxCost} onChange={e => setMaxCost(e.target.value)} />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input type="checkbox" checked={hideEmpty} onChange={e => setHideEmpty(e.target.checked)} className="w-4 h-4 rounded" />
              Tylko z kosztami
            </label>

            {hasFilters && (
              <button className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline self-start" onClick={clearFilters}>
                Wyczyść wszystkie filtry
              </button>
            )}
          </div>
        )}
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
        {([
          { label: "Projekty",         value: String(rows.length) },
          ...(showMonthCols ? [{ label: "Koszty płac",  value: `${formatAmount(sumSalary.toFixed(2))} PLN` }] : []),
          { label: "Pozostałe koszty", value: `${formatAmount(sumOther.toFixed(2))} PLN` },
          { label: "Łącznie",          value: `${formatAmount(sumTotal.toFixed(2))} PLN` },
        ] as { label: string; value: string }[]).map(({ label, value }) => (
          <div key={label} className="bg-white dark:bg-gray-800 px-3 py-2 sm:px-4 sm:py-2.5 flex flex-col sm:min-w-[100px]">
            <span className="text-[10px] sm:text-xs font-mono uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</span>
            <span className="text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100 tabular-nums">{value}</span>
          </div>
        ))}
      </div>

      {/* Mobile sort bar */}
      <div className="lg:hidden flex items-center gap-2 bg-white dark:bg-gray-800 px-3 py-2">
        <span className="text-xs font-mono uppercase tracking-wider text-gray-500 dark:text-gray-400 shrink-0">Sortuj</span>
        <select
          className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
          value={sortField} onChange={e => setSortField(e.target.value as SortField)}
        >
          {sortOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button
          type="button"
          onClick={() => setSortDir(d => d === "asc" ? "desc" : "asc")}
          className="shrink-0 w-9 h-9 flex items-center justify-center rounded border border-gray-300 dark:border-gray-600 text-[var(--color-primary)] hover:bg-gray-50 dark:hover:bg-gray-700"
          aria-label={sortDir === "asc" ? "Sortuj rosnąco" : "Sortuj malejąco"}
        >
          {sortDir === "asc" ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
        </button>
      </div>

      {/* Mobile card list */}
      <div className="lg:hidden flex flex-col gap-2">
        {rows.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 py-12 text-center text-sm text-gray-400 dark:text-gray-500">
            Brak danych dla wybranych kryteriów.
          </div>
        ) : (
          <>
            {rows.map((row) => {
              const total = parseFloat(row.other_cost) + parseFloat(row.salary_cost ?? "0");
              return (
                <div key={row.project_id} className={`bg-white dark:bg-gray-800 p-3 ${!row.is_active ? "opacity-60" : ""}`}>
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400 shrink-0">{row.project_short_name || "—"}</span>
                    <StatusBadge status={row.status} />
                    {!row.is_active && (
                      <span className="px-1.5 py-px rounded bg-red-100 text-red-500 dark:bg-red-900/30 dark:text-red-400 text-[10px] font-semibold">usunięte</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-900 dark:text-gray-100 leading-snug mb-3">{row.project_name}</div>
                  {showMonthCols && (
                    <div className="mb-3">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-1">Aktywność</div>
                      <ActivityBar pct={row.days_active_pct} segments={row.activity_segments} month={month} />
                    </div>
                  )}
                  <div className={`grid ${showMonthCols ? "grid-cols-3" : "grid-cols-2"} gap-2 pt-2 border-t border-gray-100 dark:border-gray-700`}>
                    {showMonthCols && (
                      <div className="flex flex-col min-w-0">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">Płace</span>
                        <span className="tabular-nums font-semibold text-blue-700 dark:text-blue-400 text-sm truncate"><Pln value={row.salary_cost} /></span>
                      </div>
                    )}
                    <div className="flex flex-col min-w-0">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">Pozostałe</span>
                      <span className="tabular-nums font-semibold text-gray-900 dark:text-gray-100 text-sm truncate"><Pln value={row.other_cost} /></span>
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">Łącznie</span>
                      <span className="tabular-nums font-bold text-gray-900 dark:text-gray-100 text-sm truncate"><Pln value={total} /></span>
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="bg-gray-50 dark:bg-gray-800/60 border-t-2 border-gray-200 dark:border-gray-600 p-3">
              <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                Suma — {rows.length} {rows.length === 1 ? "przedsięwzięcie" : rows.length < 5 ? "przedsięwzięcia" : "przedsięwzięć"}
              </div>
              <div className={`grid ${showMonthCols ? "grid-cols-3" : "grid-cols-2"} gap-2`}>
                {showMonthCols && (
                  <div className="flex flex-col min-w-0">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">Płace</span>
                    <span className="tabular-nums font-bold text-blue-700 dark:text-blue-400 text-sm truncate">
                      {formatAmount(sumSalary.toFixed(2))} <span className="text-[10px] font-normal text-gray-400">PLN</span>
                    </span>
                  </div>
                )}
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">Pozostałe</span>
                  <span className="tabular-nums font-bold text-gray-900 dark:text-gray-100 text-sm truncate">
                    {formatAmount(sumOther.toFixed(2))} <span className="text-[10px] font-normal text-gray-400">PLN</span>
                  </span>
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">Łącznie</span>
                  <span className="tabular-nums font-bold text-gray-900 dark:text-gray-100 text-sm truncate">
                    {formatAmount(sumTotal.toFixed(2))} <span className="text-[10px] font-normal text-gray-400">PLN</span>
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden lg:block bg-white dark:bg-gray-800 overflow-x-auto">
        {rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400 dark:text-gray-500">Brak danych dla wybranych kryteriów.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 w-28">Symbol</th>
                <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none" onClick={() => ts("project")}>
                  Przedsięwzięcie <SortIcon active={sortField === "project"} dir={sortDir} />
                </th>
                <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none w-32" onClick={() => ts("status")}>
                  Status <SortIcon active={sortField === "status"} dir={sortDir} />
                </th>
                {showMonthCols && (
                  <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none w-44" onClick={() => ts("activity")}>
                    Aktywność <SortIcon active={sortField === "activity"} dir={sortDir} />
                  </th>
                )}
                {showMonthCols && (
                  <th className="text-right px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none w-44" onClick={() => ts("salary")}>
                    Koszty płac <SortIcon active={sortField === "salary"} dir={sortDir} />
                  </th>
                )}
                <th className="text-right px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none w-52" onClick={() => ts("other")}>
                  Pozostałe koszty <SortIcon active={sortField === "other"} dir={sortDir} />
                </th>
                <th className="text-right px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none w-44" onClick={() => ts("total")}>
                  Łącznie <SortIcon active={sortField === "total"} dir={sortDir} />
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.project_id} className={`border-b border-gray-100 dark:border-gray-700/50 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/30 ${!row.is_active ? "opacity-60" : ""}`}>
                  <td className="px-4 py-2.5 font-mono text-xs">
                    <div className="text-gray-500 dark:text-gray-400">{row.project_short_name || <span className="opacity-30">—</span>}</div>
                    {!row.is_active && (
                      <span className="inline-block mt-0.5 px-1.5 py-px rounded bg-red-100 text-red-500 dark:bg-red-900/30 dark:text-red-400 text-[10px] font-semibold">usunięte</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-gray-900 dark:text-gray-100">{row.project_name}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={row.status} /></td>
                  {showMonthCols && <td className="px-4 py-2.5"><ActivityBar pct={row.days_active_pct} segments={row.activity_segments} month={month} /></td>}
                  {showMonthCols && (
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-blue-700 dark:text-blue-400">
                      <Pln value={row.salary_cost} />
                    </td>
                  )}
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-gray-900 dark:text-gray-100">
                    <Pln value={row.other_cost} />
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-bold text-gray-900 dark:text-gray-100">
                    <Pln value={parseFloat(row.other_cost) + parseFloat(row.salary_cost ?? "0")} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 dark:bg-gray-800/60 border-t-2 border-gray-200 dark:border-gray-600">
              <tr>
                <td colSpan={showMonthCols ? 4 : 3} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Suma — {rows.length} {rows.length === 1 ? "przedsięwzięcie" : rows.length < 5 ? "przedsięwzięcia" : "przedsięwzięć"}
                </td>
                {showMonthCols && (
                  <td className="px-4 py-3 text-right tabular-nums font-bold text-blue-700 dark:text-blue-400">
                    {formatAmount(sumSalary.toFixed(2))} <span className="text-xs font-normal text-gray-400">PLN</span>
                  </td>
                )}
                <td className="px-4 py-3 text-right tabular-nums font-bold text-gray-900 dark:text-gray-100">
                  {formatAmount(sumOther.toFixed(2))} <span className="text-xs font-normal text-gray-400">PLN</span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-bold text-gray-900 dark:text-gray-100">
                  {formatAmount(sumTotal.toFixed(2))} <span className="text-xs font-normal text-gray-400">PLN</span>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
        </>
      )}
    </div>
  );
}
